import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  useTheme,
  alpha,
  SxProps,
  Theme
} from '@mui/material';
import {
  Today as TodayIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import useResponsive from '../../hooks/useResponsive';

interface ResponsivePageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  userInfo?: {
    name?: string;
    email?: string;
    company?: string;
    role?: string;
  };
  actions?: React.ReactNode;
  showUserAvatar?: boolean;
  showDate?: boolean;
  showCompany?: boolean;
  gradient?: boolean;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

const ResponsivePageHeader: React.FC<ResponsivePageHeaderProps> = ({
  title,
  subtitle,
  icon,
  userInfo,
  actions,
  showUserAvatar = true,
  showDate = false,
  showCompany = false,
  gradient = false,
  children,
  sx
}) => {
  const theme = useTheme();
  const { isMobile, isTablet, getSpacing } = useResponsive();

  // Helper function for gradient styles
  const getGradientStyles = () => gradient ? {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: 'white !important',
    '& *': { color: 'white !important' },
    '& .MuiTypography-root': { color: 'white !important' },
    '& .MuiChip-root': { color: 'white !important' },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.1), transparent 50%)',
      pointerEvents: 'none'
    }
  } : { background: theme.palette.background.paper };

  // Helper function for action button styles
  const getActionStyles = () => ({
    '& .MuiButton-root': {
      backgroundColor: gradient ? alpha('#fff', 0.2) : theme.palette.primary.main,
      color: 'white',
      '&:hover': {
        backgroundColor: gradient ? alpha('#fff', 0.3) : theme.palette.primary.dark,
      }
    },
    '& .MuiIconButton-root': {
      backgroundColor: gradient ? alpha('#fff', 0.1) : alpha(theme.palette.primary.main, 0.1),
      color: gradient ? 'white' : theme.palette.primary.main,
      '&:hover': {
        backgroundColor: gradient ? alpha('#fff', 0.2) : alpha(theme.palette.primary.main, 0.2),
      }
    }
  });

  // Mobile-optimized header
  if (isMobile) {
    return (
      <Paper sx={{
        p: getSpacing(2, 3),
        mb: getSpacing(2, 3),
        borderRadius: 1,
        position: 'relative',
        overflow: 'hidden',
        ...getGradientStyles(),
        ...sx
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Main header row */}
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            {showUserAvatar && userInfo && (
              <Avatar sx={{
                width: 40,
                height: 40,
                background: gradient ? alpha('#fff', 0.2) : theme.palette.primary.main,
                fontSize: '1.2rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {icon || userInfo.name?.[0]?.toUpperCase() || userInfo.email?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            )}

            <Box flex={1}>
              <Typography variant="h6" component="h1" sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: '1.3rem',
                lineHeight: 1.2
              }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{
                  opacity: 0.8,
                  fontSize: '0.875rem'
                }}>
                  {subtitle}
                </Typography>
              )}
            </Box>

            {actions && (
              <Box sx={getActionStyles()}>
                {actions}
              </Box>
            )}
          </Box>

          {/* Info row */}
          <Box display="flex" flexDirection="column" gap={1} mt={2}>
            {showDate && (
              <Box display="flex" alignItems="center" gap={1}>
                <TodayIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {new Date().toLocaleDateString('sr-Latn-RS', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </Typography>
              </Box>
            )}

            {showCompany && userInfo?.company && (
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {userInfo.company}
                </Typography>
              </Box>
            )}

            {userInfo?.role && (
              <Chip
                label={(() => {
                  if (userInfo.role === 'admin') return 'Administrator';
                  if (userInfo.role === 'demo_customer') return 'Demo Račun';
                  return 'Korisnik';
                })()}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  backgroundColor: alpha('#fff', gradient ? 0.2 : 0.1),
                  color: gradient ? 'white' : 'inherit',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
            )}
          </Box>

          {children && (
            <Box mt={2}>
              {children}
            </Box>
          )}
        </Box>
      </Paper>
    );
  }

  // Desktop/Tablet header (existing behavior)
  return (
    <Paper sx={{
      p: getSpacing(3, 4),
      mb: getSpacing(3, 4),
      borderRadius: 1,
      position: 'relative',
      overflow: 'hidden',
      ...getGradientStyles(),
      ...getActionStyles(),
      ...sx
    }}>
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={3}>
            {showUserAvatar && (
              <Avatar sx={{
                width: isTablet ? 56 : 60,
                height: isTablet ? 56 : 60,
                background: gradient ? alpha('#fff', 0.2) : theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {icon || <TodayIcon sx={{ fontSize: isTablet ? 28 : 30 }} />}
              </Avatar>
            )}

            <Box>
              <Typography variant={isTablet ? "h4" : "h3"} component="h1" sx={{
                fontWeight: 700,
                mb: 1,
                textShadow: gradient ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
              }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant={isTablet ? "h6" : "h5"} sx={{
                  opacity: 0.9,
                  fontWeight: 400
                }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {userInfo?.role && (
              <Chip
                label={(() => {
                  if (userInfo.role === 'admin') return 'Administrator';
                  return 'Korisnik';
                })()}
                sx={{
                  backgroundColor: alpha('#fff', gradient ? 0.2 : 0.1),
                  color: gradient ? 'white' : 'inherit',
                  fontWeight: 600
                }}
              />
            )}
            {actions && (
              <Box sx={{
                '& .MuiButton-root': {
                  backgroundColor: gradient ? alpha('#fff', 0.2) : theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: gradient ? alpha('#fff', 0.3) : theme.palette.primary.dark,
                  }
                },
                '& .MuiIconButton-root': {
                  backgroundColor: gradient ? alpha('#fff', 0.1) : alpha(theme.palette.primary.main, 0.1),
                  color: gradient ? 'white' : theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: gradient ? alpha('#fff', 0.2) : alpha(theme.palette.primary.main, 0.2),
                  }
                }
              }}>
                {actions}
              </Box>
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={4} mt={3}>
          {showDate && (
            <Box display="flex" alignItems="center" gap={1}>
              <TodayIcon sx={{ opacity: 0.8 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {new Date().toLocaleDateString('sr-Latn-RS', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
          )}

          {showCompany && userInfo?.company && (
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon sx={{ opacity: 0.8 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {userInfo.company}
              </Typography>
            </Box>
          )}
        </Box>

        {children && (
          <Box mt={3}>
            {children}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ResponsivePageHeader;
